import { format } from "date-fns";
import { useMemo, useState } from "react";

import { Card } from "@/components/card";
import { useTableControl } from "@/components/table-control";
import { Badge } from "@/components/ui/badge";

import { useGetRoomSystemSessions } from "../api/get-room-system-sessions";
import { Timeframe } from "../types/timeframe";
import { generateTimeRange } from "../utils/generate-time-range";

import { RoomSystemSessionsTable } from "./room-system-sessions-table";
import { TimeframeSelector } from "./timeframe-selector";

type RoomSystemSessionsProps = {
  organisationId?: string;
  roomSystemId?: string;
};

export function RoomSystemSessions({
  organisationId,
  roomSystemId,
}: RoomSystemSessionsProps) {
  const [timeFrame, setTimeFrame] = useState<Timeframe>("w");
  const { pagination, setPagination } = useTableControl({
    initialPageSize: 10,
    persistInUrl: false,
  });

  const { from, to } = generateTimeRange(timeFrame);
  const {
    data: roomUsage,
    isSuccess,
    isLoading,
    isFetching,
  } = useGetRoomSystemSessions({
    from: from.toISOString(),
    to: to.toISOString(),
    organisationId,
    roomSystemId,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  const pageCount = useMemo(() => {
    if (!isSuccess || !roomUsage?.count) {
      return 1;
    }
    return Math.ceil(roomUsage.count / pagination.pageSize);
  }, [isSuccess, roomUsage?.count, pagination.pageSize]);

  return (
    <Card>
      <div className="flex items-center justify-between">
        <Card.Header
          title={
            <span>
              Room Usage
              <Badge variant="outline" className="ml-1 font-normal">
                {format(from, "MMM dd")} - {format(to, "MMM dd")}
              </Badge>
            </span>
          }
        />
        <TimeframeSelector
          value={timeFrame}
          onChange={(value) => {
            setTimeFrame(value as Timeframe);
            setPagination((prev) => ({
              ...prev,
              pageIndex: 0,
            }));
          }}
        />
      </div>

      <div className="relative">
        <RoomSystemSessionsTable
          data={isSuccess ? roomUsage.items : []}
          pagination={pagination}
          onPaginationChange={setPagination}
          pageCount={pageCount}
          isLoading={isLoading || isFetching}
        />
      </div>
    </Card>
  );
}
